import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * "Wat is nieuw" — de changelog in de app (issue #238).
 *
 * Aanleiding: nieuwe functies werden bij toeval ontdekt. Linda kende de kolom
 * "laatst actief" niet, kende het teamleider-vinkje niet, en vertelde extern
 * dat een integratie werkte terwijl dat alleen een voorkant was. Een regel per
 * wijziging, geschreven door een beheerder, is het goedkoopste tegengif.
 *
 * Opslag: tenants/{tenantId}/changelog/{id}.
 *
 * Bewust géén versienummers, categorieën, koppeling aan git of automatische
 * generatie: dat maakt van een gewoonte een systeem, en de gewoonte is het
 * punt. Eén boolean (`gepubliceerd`) scheidt concept van vrijgegeven; dat is
 * geen workflow en hoort er ook nooit een te worden.
 */
/** Wall-clock NL-datum, zoals de agenda. Een changelogregel is een dag, geen moment. */
const DateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet YYYY-MM-DD zijn');
export const ChangelogItem = z.object({
    id: DocId,
    tenantId: TenantId,
    title: z.string().min(1).max(160),
    /**
     * Platte tekst, geen rich text. Een changelogregel is één of twee zinnen;
     * een editor plus filter plus renderer erbij halen levert niets op wat de
     * lezer merkt. Regeleindes blijven staan bij het renderen.
     */
    body: z.string().min(1).max(1000),
    /** Datum die de lezer ziet; ook de sorteersleutel (nieuwste eerst). */
    date: DateString,
    gepubliceerd: z.boolean(),
    /**
     * Gezet bij de eerste publicatie en daarna nooit meer gewist. Dat is precies
     * wat "de bel gaat één keer" afdwingt: wie een item terugzet naar concept en
     * opnieuw publiceert, stuurt geen tweede melding naar de hele tenant.
     */
    publishedAt: IsoTimestamp.optional(),
    createdAt: IsoTimestamp,
    createdBy: z.string().min(1).max(200),
    updatedAt: IsoTimestamp,
    updatedBy: z.string().min(1).max(200),
});
export const ListChangelogResponse = z.object({
    items: z.array(ChangelogItem),
});
/**
 * De beheerlijst draagt de stand van `CHANGELOG_NOTIFY_ENABLED` mee, zodat het
 * beheerscherm niet hoeft te beloven wat de server niet doet. Als schakelaar
 * hier ooit aan de leeskant nodig is hoort hij daar apart bij; deze vlag is een
 * eigenschap van de beheeractie, niet van de pagina.
 */
export const ListChangelogAdminResponse = ListChangelogResponse.extend({
    meldingenAan: z.boolean(),
});
export const CreateChangelogItemInput = z.object({
    title: z.string().min(1).max(160),
    body: z.string().min(1).max(1000),
    date: DateString,
    gepubliceerd: z.boolean().default(false),
});
/**
 * Elk veld optioneel: de beheerder kan een concept publiceren zonder de tekst
 * opnieuw mee te sturen, en tekst bijwerken zonder de publicatiestand te raken.
 */
export const UpdateChangelogItemInput = z.object({
    title: z.string().min(1).max(160).optional(),
    body: z.string().min(1).max(1000).optional(),
    date: DateString.optional(),
    gepubliceerd: z.boolean().optional(),
});
//# sourceMappingURL=changelog.js.map